import { useCallback } from 'react';

import { useDispatch } from 'react-redux';

import { Body } from '@iris/react-components/Body';
import { Section } from '@iris/react-components/Section';
import { WalletIcon } from '@iris/react-icons/WalletIcon';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/core/device';

import { useFeatures } from '@atc/features';

import { Heading, SubHeading } from 'reaxl';
import { WalletSyncBanner } from 'reaxl-native-deal-calculator';

import paymentsDuck from '@/ducks/paymentsDuck.js';

import PriceSectionCard from './PriceSectionCard';

/**
 * PriceSection Component
 *
 * @description The main pricing section that serves as the central hub for all pricing-related
 * functionality on the Vehicle Detail Page. It combines:
 * - My Wallet functionality (KBB-branded wallet experience)
 * - Price comparison tools and market insights
 * - Lender pre-qualification options
 * - Incentives and special offers
 * - Responsive layout that adapts to different screen sizes
 *
 * The component uses feature flags to control wallet branding and functionality,
 * and adapts its layout based on device size for optimal user experience.
 *
 * @component
 * @param {Object} props - The component props
 * @param {React.ReactElement} props.renderMyWalletContainer - Rendered My Wallet container component
 * @param {React.ReactElement} props.renderLenderPreQualWrapper - Rendered lender pre-qualification wrapper
 * @param {Object} props.emailOwnerProps - Props for email-related functionality and lead generation
 * @param {Function} props.getIncentiveOffers - Function to fetch available incentive offers
 * @param {boolean} props.enabledAmdIncentivesOffers - Flag to enable Advanced Market Data incentives
 *
 */
export default function PriceSection({
    renderMyWalletContainer,
    renderLenderPreQualWrapper,
    emailOwnerProps,
    getIncentiveOffers,
    enabledAmdIncentivesOffers,
}) {
    const {
        global_kbb_branded_wallet: [enableKbbBrandedWallet, { hide_kbb_branding: hideKbbBranding }],
    } = useFeatures([
        'global_kbb_branded_wallet',
    ]);

    const dispatch = useDispatch();
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const updateMyWallet = useCallback((data) => {
        dispatch(paymentsDuck.creators.updateMyWallet(data));
    }, [dispatch]);

    const renderSubHeading = enableKbbBrandedWallet && (
        <div className="iris:flex iris:items-center iris:gap-1 iris:mt-1">
            <WalletIcon
                data-cmp="walletIcon"
                className="iris:size-[18px]"
            />
            <Body
                as="p"
                className="iris:inline iris:m-0"
            >
                <SubHeading
                    weight="normal"
                    level={4}
                >
                    {' '}
                    My Wallet
                </SubHeading>
                {!hideKbbBranding && (
                    <>
                        {' '}
                        by Kelley Blue Book
                        <span className="iris:align-super iris:text-xs">&#174;</span>
                    </>
                )}
            </Body>
        </div>
    );

    const renderMyWalletContainerHeader = (
        <div className="iris:w-full iris:mt-4 iris:sm:mt-6">
            <Heading
                level={3}
                color="primary"
            >
                Build Your Offer
            </Heading>
            <div className="iris:flex iris:flex-col iris:md:flex-row iris:gap-2 iris:justify-between">
                {renderSubHeading}
                <WalletSyncBanner
                    paymentsDuck={paymentsDuck}
                    updateMyWallet={updateMyWallet}
                />
            </div>
        </div>
    );

    return (
        <Section
            data-cmp="priceSection"
            className="iris:py-4 iris:md:py-5 iris:m-0!"
            key="price-section"
        >
            <>
                {renderMyWalletContainerHeader}
                <div className="iris:grid iris:grid-cols-1 iris:sm:grid-cols-2 iris:gap-4">
                    <div className="iris:w-full iris:mt-6">
                        <PriceSectionCard
                            emailOwnerProps={emailOwnerProps}
                            getIncentiveOffers={getIncentiveOffers}
                            enabledAmdIncentivesOffers={enabledAmdIncentivesOffers}
                        />
                        {!isXs && renderLenderPreQualWrapper}
                    </div>
                    <div className="iris:w-full iris:flex iris:flex-col">
                        {renderMyWalletContainer}
                        {isXs && renderLenderPreQualWrapper}
                    </div>
                </div>
            </>
        </Section>
    );
}
